import React, { useState, useCallback } from 'react';
import type { RichContent } from '../../types';
import type { WidgetStrings } from '../../utils/i18n';
import { DEFAULT_WIDGET_STRINGS } from '../../utils/i18n';

const ALLOWED_INPUT_TYPES = new Set([
  'text',
  'email',
  'number',
  'tel',
  'url',
  'date',
  'time',
  'datetime-local',
  'password',
  'search',
]);

interface FormMessageProps {
  content: RichContent;
  strings?: WidgetStrings;
}

const FormMessage = React.memo(function FormMessage({
  content,
  strings = DEFAULT_WIDGET_STRINGS,
}: FormMessageProps) {
  const [formData, setFormData] = useState<Record<string, string>>({});

  const handleFieldChange = useCallback((fieldName: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    content.onSubmit?.(formData);
  }, [content, formData]);

  return (
    <div className="mt-2 bg-gray-50 rounded-lg p-3">
      <h4 className="font-semibold text-sm mb-2">{content.title}</h4>
      {content.fields?.map((field, idx) => {
        const inputType = ALLOWED_INPUT_TYPES.has(field.type || '') ? field.type : 'text';
        const fieldId = `form-field-${field.name}-${idx}`;
        return (
          <div key={idx} className="mb-2">
            <label htmlFor={fieldId} className="block text-xs text-gray-700 mb-1">
              {field.label}
              {field.required && (
                <span className="text-red-600 ml-0.5" aria-hidden="true">
                  *
                </span>
              )}
            </label>
            <input
              id={fieldId}
              type={inputType}
              placeholder={field.placeholder}
              value={formData[field.name] || ''}
              onChange={e => handleFieldChange(field.name, e.target.value)}
              required={!!field.required}
              aria-required={field.required ? true : undefined}
              className="w-full px-2 py-1 text-xs border rounded focus:outline-2 focus:outline-blue-500"
            />
          </div>
        );
      })}
      <button
        type="button"
        onClick={handleSubmit}
        className="w-full mt-2 px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 focus:outline-2 focus:outline-blue-500"
      >
        {content.submitLabel || strings.submit}
      </button>
    </div>
  );
});

export default FormMessage;
